import { useState } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ActionGetInHouse } from '../../store/Actions/MasterDataAction';

/**
 * CasePaperButton Component
 * 
 * A reusable button component that:
 * 1. Shows a loading modal when clicked
 * 2. Fetches in-house booking data for the current date
 * 3. Checks if the patient has an active in-house booking
 * 4. Navigates to the case paper page if found
 * 5. Shows an error message if no active booking exists
 * 
 * @param {string} patientId - The ID of the patient
 * @param {object} buttonStyle - Optional custom styles for the button
 * @param {string} buttonText - Optional custom button text (default: "Case Paper")
 */
const CasePaperButton = ({
    patientId,
    buttonStyle = {},
    buttonText = 'Case Paper'
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { inHouse } = useSelector(state => state?.MasterData);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleButtonClick = async () => {
        if (!patientId) {
            message.error('Patient ID is required');
            return;
        }

        setIsModalVisible(true);
        setIsLoading(true);

        try {
            // Fetch in-house bookings for today
            const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
            await dispatch(ActionGetInHouse({ Query: `?date=${today}` }));

            // Wait a bit for the Redux state to update
            setTimeout(() => {
                // Find active in-house booking for the current patient
                const activeBooking = inHouse?.find(
                    (booking) => booking?.patientId?._id === patientId && booking?.status === 'ACTIVE'
                );

                setIsLoading(false);

                if (activeBooking) {
                    // Close modal and navigate to case paper page
                    setIsModalVisible(false);
                    const casePaperUrl = `/indoor-casepaper/${activeBooking?.patientId?._id}?orderId=${activeBooking?.orderId}&patientPlanId=${activeBooking?.patientPlanId}`;
                    navigate(casePaperUrl);
                } else {
                    // Show error message if no active booking found
                    message.error('No active in-house booking found for this patient');
                    setIsModalVisible(false);
                }
            }, 500); // Small delay to ensure Redux state is updated
        } catch (error) {
            setIsLoading(false);
            setIsModalVisible(false);
            message.error('Failed to fetch in-house booking data');
            console.error('Error fetching in-house data:', error);
        }
    };

    const handleModalClose = () => {
        if (!isLoading) {
            setIsModalVisible(false);
        }
    };

    const defaultButtonStyle = {
        background: '#8b5cf6',
        color: '#fff',
        borderRadius: '8px',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontWeight: '500',
        transition: 'all 0.2s ease',
        height: 'auto',
        ...buttonStyle,
    };

    return (
        <>
            <Button
                onClick={handleButtonClick}
                className='btn'
                style={defaultButtonStyle}
            >
                <FeatherIcon icon="clipboard" size={16} />
                {buttonText}
            </Button>

            <Modal
                open={isModalVisible}
                closable={!isLoading}
                footer={null}
                onCancel={handleModalClose}
                centered
                styles={{
                    body: {
                        padding: '40px',
                        textAlign: 'center',
                    },
                    mask: {
                        backdropFilter: 'blur(10px)',
                    },
                    content: {
                        borderRadius: 16,
                    },
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                    <Spin size="large" />
                    <div>
                        <h3 style={{ marginBottom: '8px', fontSize: '18px', fontWeight: '600' }}>
                            Loading Case Paper
                        </h3>
                        <p style={{ color: '#666', margin: 0 }}>
                            Please wait while we fetch the patient&apos;s in-house booking details...
                        </p>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default CasePaperButton;
